import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { toJsonLd } from '../seo/jsonLd';
import { getSeoConfig } from '../seo/routeRegistry';
import OG_IMAGES from '../seo/og-images.json';

const BASE_URL = 'https://www.bluecmr.com';

/**
 * SEO por ruta e idioma (routeRegistry). Solo se consulta en las variantes de
 * idioma: las páginas fijan su title/description en español, así que sin esto
 * /en/platform se servía con el título español aunque su contenido ya estaba
 * traducido. Las rutas sin entrada en el registro conservan lo que pasa la
 * página.
 */
const LOCALIZED_SEO = getSeoConfig();

/** Prefijos de idioma servidos bajo su propia URL. */
const LANG_PREFIX = /^\/(en|fr)(\/|$)/;

/**
 * Normaliza cualquier URL absoluta del dominio apex (https://bluecmr.com) al
 * subdominio con www (https://www.bluecmr.com), que es el dominio real servido
 * por CloudFront. El apex hace 301 a www en OVH, por lo que el canonical debe
 * apuntar directamente a www para evitar el bucle canonical→redirección que
 * impedía la indexación (página con redirección / alternativa canónica).
 */
const toWww = (url) =>
  typeof url === 'string'
    ? url.replace(/^https:\/\/bluecmr\.com/i, 'https://www.bluecmr.com')
    : url;

/**
 * Reusable SEO Meta component.
 * Props:
 *  - title: string
 *  - description: string
 *  - canonical: string (absolute URL)
 *  - alternates: object { es: url, en: url, fr: url, 'x-default': url }
 *  - image: string (absolute URL to OG image). Normalmente no se pasa: sale de
 *    src/seo/og-images.json por ruta (1200×630, scripts/gen-og-images.js).
 *  - type: Open Graph type (default website/article)
 *  - locale: locale code (default es_ES)
 *  - siteName: site name (default BlueCMR)
 *  - keywords: optional comma separated keywords
 *  - breadcrumbs: optional array [{ name, url }]
 *  - structuredData: optional array of plain JS objects (already schema ready)
 *  - noindex: excluir la página de los índices (landings de campaña, reservas…)
 */
const SeoMeta = ({
  title,
  description,
  canonical,
  alternates,
  image,
  imageWidth,
  imageHeight,
  type = 'website',
  locale = 'es_ES',
  siteName = 'BlueCMR',
  keywords,
  breadcrumbs,
  structuredData,
  noindex = false
}) => {
  // Normalizar todas las URLs del dominio apex a www (dominio real servido).
  //
  // En las variantes de idioma (/en/…) el canonical NO puede ser el que pasa la
  // página, porque las páginas fijan a mano la URL española. Un canonical hacia
  // el español le dice a Google que la versión inglesa es un duplicado y no la
  // indexa — lo que además invalida el hreflang, que exige canonical propio en
  // cada variante. Ahí el canonical se deriva de la ruta actual.
  //
  // Y si la página no pasa canonical (las legales, por ejemplo), se usa también
  // la ruta actual: toda página indexable debe declarar el suyo. Antes lo tapaba
  // el hook useCanonicalUrl inyectándolo a mano fuera de Helmet, lo que además
  // provocaba canonicals duplicados por carrera con el propio Helmet.
  const { pathname } = useLocation();
  const isLanguageVariant = LANG_PREFIX.test(pathname);
  const selfPath = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const canonicalUrl =
    isLanguageVariant || !canonical ? `${BASE_URL}${selfPath}` : toWww(canonical);

  // En las variantes de idioma, title/description salen del registro de rutas.
  const localized = isLanguageVariant ? LOCALIZED_SEO[selfPath] : undefined;
  const finalTitle = localized?.title || title;
  const finalDescription = localized?.description || description;
  const finalKeywords = localized?.keywords || keywords;
  // Imagen para compartir: la de la ruta en og-images.json o, si no tiene, la
  // tarjeta azul marino en el idioma de la ruta (nov-2026; antes bluecmr.jpeg).
  const ogFile = OG_IMAGES.pages[selfPath] || (isLanguageVariant ? OG_IMAGES.defaultEn : OG_IMAGES.defaultEs);
  const imageUrl = toWww(image || `${OG_IMAGES.base}${ogFile}`);
  const ogWidth = image ? imageWidth : 1200;
  const ogHeight = image ? imageHeight : 630;
  const normalizedAlternates = alternates
    ? Object.fromEntries(Object.entries(alternates).map(([lang, url]) => [lang, toWww(url)]))
    : alternates;
  const normalizedBreadcrumbs = breadcrumbs
    ? breadcrumbs.map((b) => ({ ...b, url: toWww(b.url) }))
    : breadcrumbs;

  const scripts = [];

  if (normalizedBreadcrumbs && normalizedBreadcrumbs.length) {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: normalizedBreadcrumbs.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.url
      }))
    };
    scripts.push(breadcrumbSchema);
  }

  if (structuredData && structuredData.length) {
    // Normalizar URLs apex→www en el JSON-LD serializado (Article, FAQ, etc.).
    structuredData.forEach(obj => {
      const normalized = JSON.parse(
        JSON.stringify(obj).replace(/https:\/\/bluecmr\.com/gi, 'https://www.bluecmr.com')
      );
      scripts.push(normalized);
    });
  }

  return (
    <Helmet htmlAttributes={{ lang: isLanguageVariant ? selfPath.split('/')[1] : 'es' }}>
      {finalTitle && <title>{finalTitle}</title>}
      {finalDescription && <meta name="description" content={finalDescription} />}
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {normalizedAlternates && Object.entries(normalizedAlternates).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      {/* La prop `noindex` se ignoraba: 4 páginas que la pasaban (/meeting,
          /reserva, las landings de campaña) se servían como "index, follow". */}
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <meta
        name="googlebot"
        content={
          noindex
            ? 'noindex, follow'
            : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {finalTitle && <meta property="og:title" content={finalTitle} />}
      {finalDescription && <meta property="og:description" content={finalDescription} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {ogWidth && <meta property="og:image:width" content={String(ogWidth)} />}
      {ogHeight && <meta property="og:image:height" content={String(ogHeight)} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {finalTitle && <meta name="twitter:title" content={finalTitle} />}
      {finalDescription && <meta name="twitter:description" content={finalDescription} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {/* El JSON va como hijo de texto: Helmet descarta los <script> que solo
          traen dangerouslySetInnerHTML (ver src/seo/jsonLd.js). */}
      {scripts.map((schema, i) => (
        <script key={i} type="application/ld+json">{toJsonLd(schema)}</script>
      ))}
    </Helmet>
  );
};

export default SeoMeta;
